/** Free booking only. POST /api/booking/{orderId}/Finalize body `response`. */
export enum BookingFinalizeResponse {
  Complete = 1,
  DidNotAttend = 2,
}

/**
 * Payment source — matches backend `PaymentSource : byte` enum.
 * Used in api/Booking request body (`paymentMethod` field) and transaction responses.
 */
export enum PaymentSource {
  Wallet = 1,
  Card = 2,
  Bank = 3,
  System = 4,
  UPI = 5,
  Promo = 6
}

/** Resolve paymentMethod + provideCardId for api/Booking request body. */
export function buildBookingPaymentPayload(input: {
  useWallet: boolean;
  useUpi: boolean;
  cardLookupId?: string | null;
}): { paymentMethod: PaymentSource; provideCardId: string | null } {
  if (input.useWallet) {
    return { paymentMethod: PaymentSource.Wallet, provideCardId: '' };
  }
  if (input.useUpi) {
    return { paymentMethod: PaymentSource.UPI, provideCardId: null };
  }
  const cardId = typeof input.cardLookupId === 'string' ? input.cardLookupId.trim() : '';
  return {
    paymentMethod: PaymentSource.Card,
    provideCardId: cardId.length > 0 ? cardId : '',
  };
}

/** Resolve orderNumber from booking API envelope or inner data payload. */
export function getOrderNumberFromBookingResponse(response: unknown): string {
  const r = response as {
    order?: { orderNumber?: string };
    data?: { order?: { orderNumber?: string }; orderNumber?: string };
  } | null | undefined;
  if (!r) return '';
  return r.order?.orderNumber ?? r.data?.order?.orderNumber ?? r.data?.orderNumber ?? '';
}

/** Display label for payment source (for billing/transaction UI). */
export function getPaymentSourceLabel(paymentSource: number | undefined): string {
  switch (paymentSource) {
    case PaymentSource.Wallet: return 'Wallet';
    case PaymentSource.Card: return 'Card';
    case PaymentSource.Bank: return 'Bank';
    case PaymentSource.System: return 'System';
    case PaymentSource.UPI: return 'UPI';
    case PaymentSource.Promo: return 'Promo';
    default: return 'Other';
  }
}

/**
 * Entity type for payment checkout.
 * Used in api/Payment/checkout request body.
 */
export enum EntityType {
  Order = 5
}

/**
 * Approval status for booking (matches backend ApprovalStatus enum).
 * Used in GetClientBookings, GetConsultantBookings, GetOrdersByUserId, and Status API payloads.
 */
export enum ApprovalStatus {
  All = 0,
  Pending = 1,
  Approved = 2,
  Rejected = 3,
  Overdue = 4,
  Cancelled = 5,
  Completed = 6,
  Submitted = 7,
  Reschedule = 8,
  PaymentPending = 9,
  RescheduleRequested = 10
}

const STATUS_STRING_TO_ENUM: Record<string, ApprovalStatus> = {
  PENDING: ApprovalStatus.Pending,
  APPROVED: ApprovalStatus.Approved,
  REJECTED: ApprovalStatus.Rejected,
  OVERDUE: ApprovalStatus.Overdue,
  CANCELLED: ApprovalStatus.Cancelled,
  COMPLETED: ApprovalStatus.Completed,
  SUBMITTED: ApprovalStatus.Submitted,
  RESCHEDULE: ApprovalStatus.Reschedule,
  PAYMENTPENDING: ApprovalStatus.PaymentPending,
  RESCHEDULE_REQUESTED: ApprovalStatus.RescheduleRequested,
  pending: ApprovalStatus.Pending,
  approved: ApprovalStatus.Approved,
  rejected: ApprovalStatus.Rejected,
  overdue: ApprovalStatus.Overdue,
  cancelled: ApprovalStatus.Cancelled,
  completed: ApprovalStatus.Completed,
  submitted: ApprovalStatus.Submitted,
  reschedule: ApprovalStatus.Reschedule,
  paymentpending: ApprovalStatus.PaymentPending,
  reschedulerequested: ApprovalStatus.RescheduleRequested,
  Rejected: ApprovalStatus.Rejected,
  Cancelled: ApprovalStatus.Cancelled,
  Approved: ApprovalStatus.Approved,
  PaymentPending: ApprovalStatus.PaymentPending,
  RescheduleRequested: ApprovalStatus.RescheduleRequested,
};

/**
 * ReasonCode for DisputeType.ConsultantNoShow (api/dispute payload).
 * Sent as string in reasonCode field; for Other, description is required.
 */
export const ConsultantNoShowReasonCode = {
  ServiceNotDeliveredAsDescribed: 'ServiceNotDeliveredAsDescribed',
  PoorQualityNotSatisfactory: 'PoorQualityNotSatisfactory',
  OtherPersonDidNotAttend: 'OtherPersonDidNotAttend',
  SessionDurationShorterThanAgreed: 'SessionDurationShorterThanAgreed',
  Other: 'Other',
} as const;

export type ConsultantNoShowReasonCodeType = typeof ConsultantNoShowReasonCode[keyof typeof ConsultantNoShowReasonCode];

/** Convert Raise Dispute dialog reason value to ConsultantNoShowReasonCode string for api/dispute. */
export function toConsultantNoShowReasonCode(dialogReason: string): ConsultantNoShowReasonCodeType {
  const map: Record<string, ConsultantNoShowReasonCodeType> = {
    service_not_delivered_as_described: ConsultantNoShowReasonCode.ServiceNotDeliveredAsDescribed,
    poor_quality_not_satisfactory: ConsultantNoShowReasonCode.PoorQualityNotSatisfactory,
    other_person_did_not_attend: ConsultantNoShowReasonCode.OtherPersonDidNotAttend,
    session_duration_shorter_than_agreed: ConsultantNoShowReasonCode.SessionDurationShorterThanAgreed,
    other: ConsultantNoShowReasonCode.Other,
  };
  return map[dialogReason] ?? ConsultantNoShowReasonCode.Other;
}

/** Map API dispute reasonCode (or stored history string) to dispute dialog labels — does not change free-text descriptions. */
const CONSULTANT_NO_SHOW_REASON_LABELS: Record<string, string> = {
  [ConsultantNoShowReasonCode.ServiceNotDeliveredAsDescribed]: 'Service not delivered as described',
  [ConsultantNoShowReasonCode.PoorQualityNotSatisfactory]: 'Poor quality / Not satisfactory',
  [ConsultantNoShowReasonCode.OtherPersonDidNotAttend]: 'The other person did not attend',
  [ConsultantNoShowReasonCode.SessionDurationShorterThanAgreed]: 'Session duration shorter than agreed',
  [ConsultantNoShowReasonCode.Other]: 'Other',
};

export function formatConsultantNoShowReasonForDisplay(raw: string | undefined | null): string {
  if (raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  return CONSULTANT_NO_SHOW_REASON_LABELS[s] ?? s;
}

/** Convert string status or BookingStatus to ApprovalStatus enum number for API payload. Returns 0 for 'all' or unknown. */
export function toApprovalStatus(value: string | number | undefined): number {
  if (value === undefined || value === null) return 0;
  if (typeof value === 'number' && value >= 0 && value <= 10) return value;
  if (value === 'all' || value === '') return 0;
  return STATUS_STRING_TO_ENUM[String(value)] ?? 0;
}

/** Numeric ApprovalStatus to UI string key (PENDING, APPROVED, etc.) for use with status maps. */
const APPROVAL_STATUS_TO_KEY: Record<number, string> = {
  [ApprovalStatus.Pending]: 'PENDING',
  [ApprovalStatus.Approved]: 'APPROVED',
  [ApprovalStatus.Rejected]: 'REJECTED',
  [ApprovalStatus.Overdue]: 'OVERDUE',
  [ApprovalStatus.Cancelled]: 'CANCELLED',
  [ApprovalStatus.Completed]: 'COMPLETED',
  [ApprovalStatus.Submitted]: 'SUBMITTED',
  [ApprovalStatus.Reschedule]: 'RESCHEDULE',
  [ApprovalStatus.PaymentPending]: 'PAYMENTPENDING',
  [ApprovalStatus.RescheduleRequested]: 'RESCHEDULE_REQUESTED',
};

/** Normalize API status (number or string) to BookingStatus-style key for UI lookups. */
export function toBookingStatusKeyForDisplay(status: number | string | undefined): string {
  if (status === undefined || status === null) return '';
  if (typeof status === 'number') return APPROVAL_STATUS_TO_KEY[status] ?? String(status);
  const s = String(status).trim();
  // API may send numeric status as string (e.g. "9" for Payment Pending)
  const num = Number(s);
  if (!Number.isNaN(num) && APPROVAL_STATUS_TO_KEY[num]) return APPROVAL_STATUS_TO_KEY[num];
  return s.toUpperCase();
}

/**
 * Transaction purpose from backend (TransactionPurpose enum).
 * Used to pick PDF and bind data per transaction type from booking transactions response.
 */
export const TransactionPurpose = {
  ServicePayment: 1,      // Payment for order / project / consultation
  WalletTopUp: 2,         // Add funds to wallet
  Withdrawal: 3,          // User withdraws wallet balance
  Refund: 4,              // Return money to user
  Commission: 5,          // Platform revenue from service payment
  ReferralReward: 6,      // Referral incentive payout
  Subscription: 7,        // Membership / plan purchase
  Adjustment: 8,          // Manual admin correction (credit/debit)
  Penalty: 9,             // Fine, dispute fee, platform charge
  Chargeback: 10,         // Bank-initiated reversal
  DisputeSettlement: 11,  // Dispute settlement
} as const;

/**
 * Transaction entity type — matches backend `TransactionEntityType : byte` enum.
 * Used on billing / wallet / payment history transaction payloads (`entityType`).
 */
export enum TransactionEntityType {
  Order = 1,
  Project = 2,
  Subscription = 3,
  Payout = 4,
  Referral = 5,
  /** Talent Pool workspace-task payments. `entityId` is the milestone id. Legacy task rows stay as Project (2). */
  Task = 6,
}

export const TRANSACTION_ENTITY_TYPE_LABELS: Record<TransactionEntityType, string> = {
  [TransactionEntityType.Order]: 'Order',
  [TransactionEntityType.Project]: 'Project',
  [TransactionEntityType.Subscription]: 'Subscription',
  [TransactionEntityType.Payout]: 'Payout',
  [TransactionEntityType.Referral]: 'Referral',
  [TransactionEntityType.Task]: 'Task',
};

export function parseTransactionEntityType(value: unknown): TransactionEntityType | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number' && value in TRANSACTION_ENTITY_TYPE_LABELS) {
    return value as TransactionEntityType;
  }
  const asNum = Number(value);
  if (!Number.isNaN(asNum) && asNum in TRANSACTION_ENTITY_TYPE_LABELS) {
    return asNum as TransactionEntityType;
  }
  const normalized = String(value).trim().toLowerCase();
  const byName = (Object.entries(TRANSACTION_ENTITY_TYPE_LABELS) as [string, string][]).find(
    ([, label]) => label.toLowerCase() === normalized
  );
  return byName ? (Number(byName[0]) as TransactionEntityType) : null;
}

export function getTransactionEntityTypeLabel(value: unknown): string {
  const parsed = parseTransactionEntityType(value);
  return parsed != null ? TRANSACTION_ENTITY_TYPE_LABELS[parsed] : '';
}

export function isTransactionEntityType(
  value: unknown,
  expected: TransactionEntityType
): boolean {
  return parseTransactionEntityType(value) === expected;
}

/** Project (2) and Task (6) both resolve through the milestone summary. Old task rows are still stored as Project. */
export function isProjectOrMilestoneEntityType(value: unknown): boolean {
  return (
    isTransactionEntityType(value, TransactionEntityType.Project) ||
    isTransactionEntityType(value, TransactionEntityType.Task)
  );
}

/** Helper: get PDF URL from a wrapper item if present and non-empty. */
function getPdfFromItem(item: any): string | null {
  if (item?.pdf == null) return null;
  const s = String(item.pdf).trim();
  return s !== '' ? s : null;
}

/**
 * Parse /api/Payment/BookingTransactions/{BookingId} response.
 * API may return data: Array<{ transaction, pdf? }> or data: Array<transaction>.
 * PDF is picked per purpose individually from each row (each row has its own pdf).
 * Returns { transactions, hasInvoicePdf, invoicePdfUrl, refundPdfUrl }.
 * - invoicePdfUrl: pdf from row where purpose === ServicePayment (1) → "Download Invoice" in booking detail.
 * - refundPdfUrl: pdf from row where purpose === Refund (4) → "Download Receipt" in Refund Details card.
 */
export function parseBookingTransactionsResponse(raw: any[]): { transactions: any[]; hasInvoicePdf: boolean; invoicePdfUrl: string | null; refundPdfUrl: string | null } {
  if (!Array.isArray(raw) || raw.length === 0) return { transactions: [], hasInvoicePdf: false, invoicePdfUrl: null, refundPdfUrl: null };
  const isWrapperShape = raw[0]?.transaction != null;
  const transactions = isWrapperShape
    ? normalizeBookingTransactions(raw.map((x: any) => x.transaction).filter(Boolean))
    : normalizeBookingTransactions(raw);
  let invoicePdfUrl: string | null = null;
  let refundPdfUrl: string | null = null;
  if (isWrapperShape) {
    // Prefer the ServicePayment / Refund row that actually has a PDF.
    // API can return multiple purpose=1 rows (e.g. wallet credit + card debit); first may have pdf: null.
    const paymentItem = raw.find(
      (x: any) =>
        x.transaction?.purpose === TransactionPurpose.ServicePayment &&
        getPdfFromItem(x)
    );
    invoicePdfUrl = paymentItem ? getPdfFromItem(paymentItem) : null;
    const refundItem = raw.find(
      (x: any) =>
        x.transaction?.purpose === TransactionPurpose.Refund &&
        getPdfFromItem(x)
    );
    refundPdfUrl = refundItem ? getPdfFromItem(refundItem) : null;
  }
  const hasInvoicePdf = invoicePdfUrl != null && invoicePdfUrl !== '';
  return {
    transactions,
    hasInvoicePdf,
    invoicePdfUrl: hasInvoicePdf ? invoicePdfUrl : null,
    refundPdfUrl: refundPdfUrl != null && refundPdfUrl !== '' ? refundPdfUrl : null,
  };
}

/**
 * Open invoice PDF in new tab (same as paymentHistory Download Invoice).
 * pdfValue can be: full URL (http/https), data URL (data:application/pdf;base64,...), or raw base64.
 */
export function openInvoicePdfInNewTab(pdfValue: string | null | undefined): void {
  if (!pdfValue || String(pdfValue).trim() === '') return;
  const s = String(pdfValue).trim();
  const isUrl = /^https?:\/\//i.test(s);
  const isDataUrl = /^data:/i.test(s);
  const url = isUrl || isDataUrl ? s : `data:application/pdf;base64,${s}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/** Normalize a single transaction row from BookingTransactions API. */
function normalizeOneBookingTransaction(tx: any): any {
  if (!tx || typeof tx !== 'object') return tx;
  const paymentSource = tx.paymentSource as number | undefined;
  const isCard = paymentSource === PaymentSource.Card || !!tx.paymentMethodId;
  const amount = tx.netAmount ?? tx.grossAmount ?? tx.baseAmount ?? 0;
  const paymentStatus = tx.status === 4 ? 'PAID' : (tx.status === 1 ? 'PENDING' : 'PAID');
  const normalized: any = {
    ...tx,
    isCardTransaction: isCard,
    amount: Number(amount),
    paymentStatus,
    transactionCode: tx.transactionNumber || tx.id || '',
    transactionDate: tx.completedAt || tx.createdAt,
  };
  if (isCard) {
    const cardLast4 = [tx.cardSuffix, tx.cardLast4, tx.gatewayAttempts?.[0]?.cardLast4]
      .find(v => v != null && String(v).trim() !== '');
    const cardSuffix = cardLast4 != null ? String(cardLast4).trim() : '';
    normalized.cardEnding = cardSuffix;
    normalized.transactionJSON = JSON.stringify({
      transaction_id: tx.transactionNumber || tx.id,
      id: tx.id,
      card_suffix: cardSuffix,
    });
  }
  return normalized;
}

/**
 * Pick the transaction that holds the real consultation charge (card spend or parent of wallet credit).
 * API may return wallet-credit first (processingFee 0) and card payment second — billing must use the card row.
 */
export function getPrimaryBillingTransaction(transactions: any[]): any | null {
  if (!Array.isArray(transactions) || transactions.length === 0) return null;

  const cardSpend = transactions.find(
    (tx) => tx.paymentSource === PaymentSource.Card && tx.entryType === 1
  );
  if (cardSpend) return cardSpend;

  const cardAny = transactions.find((tx) => tx.paymentSource === PaymentSource.Card);
  if (cardAny) return cardAny;

  const withParent = transactions.find((tx) => tx.parentTransaction);
  if (withParent?.parentTransaction) {
    const parent = normalizeOneBookingTransaction(withParent.parentTransaction);
    return {
      ...parent,
      address: parent.address ?? withParent.address,
    };
  }

  return transactions.reduce((best, tx) => {
    const bestGross = Number(best?.grossAmount) || 0;
    const txGross = Number(tx.grossAmount) || 0;
    return txGross >= bestGross ? tx : best;
  }, transactions[0]);
}

/**
 * Normalize /api/Payment/BookingTransactions/{BookingId} response to the shape expected by UI.
 * Backend paymentSource: 1=Wallet, 2=Card, 3=Bank, 4=System, 5=UPI, 6=Promo (PaymentSource enum).
 * New API returns: { status, statusCode, message, data: Array<{ paymentSource, paymentMethodId, baseAmount, grossAmount, netAmount, transactionNumber, status, ... }> }
 * UI expects: Array<{ isCardTransaction, paymentSource, amount, transactionJSON?, transactionCode?, paymentStatus?, transactionDate?, ... }>
 */
export function normalizeBookingTransactions(rows: any[]): any[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((tx) => {
    const normalized = normalizeOneBookingTransaction(tx);
    if (tx?.parentTransaction) {
      normalized.parentTransaction = normalizeOneBookingTransaction(tx.parentTransaction);
    }
    return normalized;
  });
}
